import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import { ForumLink } from '@/models/ForumLink';
import ParticipationService from '@/services/ParticipationService';
import { ParticipationType } from '@/models/ParticipationType';
import BCXParticipateLinks from '@/components/navigation/panels/BCXParticipateLinks.vue';
import BCXTextArea from '@/components/molecules/forms/BCXTextArea.vue';
import BCXAvatar from '@/components/molecules/BCXAvatar.vue';
import X from '@/components/svg-components/X.vue';
import Chevron from '@/components/svg-components/Chevron.vue';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import BCXSearchSelectNew from '@/components/molecules/BCXSearchSelect.vue';
import useResponsiveness from '@/mixins/useResponsiveness';
import useUser from '@/mixins/useUser';
import { i18nToItemList } from '@/utils/selectItemTools';
import useI18n from '@/mixins/useI18n';
import { templateRef, useDebounceFn, whenever } from '@vueuse/core';
import { createVuexHelpers } from 'vue2-helpers';
import { RawLocation } from 'vue-router';
import { logError } from '@/utils/logger';
import { useRouter } from 'vue2-helpers/vue-router';
import usePageState from '@/state/pageState';
import getAxiosResponseErrorCode from '@/utils/getAxiosResponseErrorCode';
import escape from 'lodash/escape';


export default /*#__PURE__*/_defineComponent({
  __name: 'BCXParticipate',
  setup(__props) {

const { tUserFallback } = useUser('self');
const tUser = tUserFallback;

const category = ref('');
const text = ref('');
const title = ref('');
const linkList = ref<ForumLink[]>([]);
const isTextAreaBig = ref(false);
const isSubmitted = ref(false);
const bigAreaHeight = ref(125);
const maxLength = 5000;
const el = templateRef<HTMLElement>('el');
const createdThreadLink = ref<RawLocation | null>(null);

const { t } = useI18n();
const { isMobileWidth } = useResponsiveness();
const { user } = useUser('self');
const router = useRouter();
const { globalErrorMessage } = usePageState();

const categoryItems = i18nToItemList('participate.categories');
const inputEnabled = computed(() => category.value !== '');
const canSend = computed(() => !!title.value.trim().length
  && !!text.value.trim().length
  && !!category.value
  && !isSubmitted.value);
const canSearch = computed(() => !!title.value.trim().length && !!category.value);

const getDefaultText = () => t('participate.default_text').toString();
const getDefaultBugText = () => `${getDefaultText()}${t('participate.default_text_bug').toString()}`;

const formatFeedbackTextForForum = (value: string) => {
  const normalized = value.replace(/\r\n?/g, '\n');
  return escape(normalized).replace(/\n/g, '<br>');
};

const { useMutations } = createVuexHelpers();
const { setIsParticipateVisible }: any = useMutations('page', ['setIsParticipateVisible']);

const search = async () => {
  if (canSearch.value) {
    const links = await ParticipationService.getMatchingTopics(title.value, category.value);
    if (Array.isArray(links)) {
      if (links?.length > 10) links.length = 10;
      linkList.value = links;
    } else {
      linkList.value = [];
    }
  }
};

const debouncedSearch = useDebounceFn(search, 500);

text.value = getDefaultText();

const setEnlargedHeight = () => {
  const elements = Array.from(el.value.querySelectorAll('.c-hidden-when-big'));
  let height = 0;
  elements.forEach((element: Element) => {
    height += element.getBoundingClientRect().height;
  });
  bigAreaHeight.value = height - 32; // Substract Text row bottom padding
};

const resetForm = () => {
  isSubmitted.value = false;
  title.value = '';
  category.value = '';
  text.value = '';
  isTextAreaBig.value = false;
  linkList.value = [];
  createdThreadLink.value = null;
};

const closePanel = () => {
  if (isSubmitted.value) {
    resetForm();
  }
  setIsParticipateVisible(false);
};

const closeDesktopPanel = () => {
  if (!isMobileWidth.value) {
    closePanel();
  }
};

whenever(isTextAreaBig, () => {
  setEnlargedHeight();
});

watch([title, category], () => debouncedSearch());

// Set default text for BUG category if no text is entered
watch(category, (newCategory) => {
  if (newCategory !== 'BUG') {
    return;
  }
  const defaultText = getDefaultText();
  const defaultBugText = getDefaultBugText();
  const userHasEnteredText = !!text.value.trim().length
    && text.value !== defaultText
    && text.value !== defaultBugText;

  if (!userHasEnteredText) {
    text.value = defaultBugText;
  }
});

const submit = async () => {
  try {
    const formattedText = formatFeedbackTextForForum(text.value);
    const createdThread = await ParticipationService.addParticipationTopic(title.value, category.value as ParticipationType, formattedText);
    createdThreadLink.value = {
      name: 'thread',
      params: {
        forumId: createdThread.forumId,
        groupId: createdThread.groupId,
        threadId: createdThread.threadId
      }
    };
    isSubmitted.value = true;
  } catch (error) {
    globalErrorMessage.value = {
      i18Key: 'forum.errors.could-not-create',
      code: getAxiosResponseErrorCode(error),
      component: 'BCXParticipate.vue',
      withoutReload: true
    };
    logError(error);
  }
};

const openCreatedThread = async () => {
  if (!createdThreadLink.value) {
    return;
  }

  try {
    await router.push(createdThreadLink.value);
    closePanel();
  } catch (error) {
    logError(error);
  }
};


return { __sfc: true,tUserFallback, tUser, category, text, title, linkList, isTextAreaBig, isSubmitted, bigAreaHeight, maxLength, el, createdThreadLink, t, isMobileWidth, user, router, globalErrorMessage, categoryItems, inputEnabled, canSend, canSearch, getDefaultText, getDefaultBugText, formatFeedbackTextForForum, useMutations, setIsParticipateVisible, search, debouncedSearch, setEnlargedHeight, resetForm, closePanel, closeDesktopPanel, submit, openCreatedThread, BCXParticipateLinks, BCXTextArea, BCXAvatar, X, Chevron, BCXMarkdown, BCXSearchSelectNew }
}

})